import React, { FC } from "react";
import { EarnedBadge } from "@shared/models";

import { BadgeCardViewModel } from "../../Badges/buildBadgeCards";
import BadgeCard from "../../Badges/BadgeCard/BadgeCard";
import { earnedBadgeToViewModel } from "./earnedBadgeToViewModel";

import "./index.scss";

interface BadgeStripProps {
  badges: EarnedBadge[];
  maxVisible?: number;
  onBadgeClick: (badge: BadgeCardViewModel) => void;
}

const DEFAULT_MAX_VISIBLE = 8;

/**
 * A horizontal, single-row strip of a viewed profile's earned badges, shown above their
 * Recognitions tab (923-core-context context/engineering/spark-rewards/phase-37-implementation-plan.md §2). Reuses `BadgeCard` directly rather than a
 * category-grouped layout (the now-deleted `BadgeCategoryGroup`, which was flex-wrap plus a category
 * heading, neither of which a strip wants) (D7) — mapped via `earnedBadgeToViewModel` (D6). Renders
 * the first `maxVisible` (default 8)
 * cards plus a "+N" overflow chip when there are more (D8); renders `null` for an empty list (no
 * wrapper, no chip), so a zero-badge profile shows no empty box or layout gap.
 *
 * `badges` is expected already ordered by the caller — `earned` arrives from the api ordered
 * `awarded_at DESC, id DESC` (api/app/services/badge.service.ts:140-141), so "first `maxVisible`"
 * means "the most recently awarded badges." This component does not sort.
 *
 * Presentational only — no dispatch/select of its own; `onBadgeClick` is a required prop supplied by
 * `UserRecognitionsContainer`, matching this epic's own "container owns the dispatch" convention.
 */
const BadgeStrip: FC<BadgeStripProps> = (props) => {
  const { badges, maxVisible = DEFAULT_MAX_VISIBLE, onBadgeClick } = props;

  if (badges.length === 0) {
    return null;
  }

  const visibleBadges = badges.slice(0, maxVisible);
  const overflowCount = badges.length - visibleBadges.length;

  return (
    <div className="badge-strip">
      {visibleBadges.map((badge) => {
        const viewModel = earnedBadgeToViewModel(badge);
        return <BadgeCard key={viewModel.definitionId} badge={viewModel} onBadgeClick={onBadgeClick} />;
      })}
      {overflowCount > 0 ? (
        <div className="badge-strip-overflow-chip" data-testid="badge-strip-overflow-chip">
          +{overflowCount}
        </div>
      ) : null}
    </div>
  );
};

export default BadgeStrip;
